import React, { FC, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { Button, Form, Input, message, Modal } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import OWButton from '../ow_button/OWButton';
import UserGroupMemberSelect from './UserGroupMemberSelect';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { userGroupsRestCrudThunks } from '../../thunks/user_groups_thunks';
import { withUserType } from '../withUserType';

const FORM_NAME = 'user-group-form';

const UserGroupFormModal: FC<any> = ({
	userGroup,
	onCancel,
	onSuccess,
	saving,
	createUserGroup,
	updateUserGroup,
}): React.ReactElement => {
	const [form] = useForm();
	const isEdit = useMemo(() => !!nullSafeGet('id', userGroup), [userGroup]);

	// The modal is mounted only while open, so these seed the fields once on mount.
	const initialValues = useMemo(
		() => ({
			name: nullSafeGetOrElse('name', userGroup, ''),
			memberEmails: nullSafeGetOrElse('memberEmails', userGroup, []),
		}),
		[userGroup]
	);

	const selectedEmails = Form.useWatch('memberEmails', form);
	const selectedCount = (selectedEmails || []).length;

	const clearAllUsers = useCallback(() => {
		form.setFieldsValue({ memberEmails: [] });
	}, [form]);

	const onSubmit = useCallback(
		(values) => {
			// The controller reads exactly two fields (`name` required, `memberEmails` optional);
			// facility id, createdBy/updatedBy and the counts are all server-derived. `id` is
			// kept on the object only because the update thunk builds its URL from it.
			const entity = {
				id: nullSafeGet('id', userGroup),
				name: values.name,
				memberEmails: values.memberEmails || [],
			};
			const messageKey = 'user-group-form-op';
			message.loading({
				content: isEdit ? 'Saving user group…' : 'Creating user group…',
				key: messageKey,
			});
			const action = isEdit ? updateUserGroup : createUserGroup;
			action(entity)
				.then((saved) => {
					message.success({
						content: isEdit ? 'User group updated' : 'User group created',
						key: messageKey,
						duration: 1.5,
					});
					if (onSuccess) {
						onSuccess(saved);
					}
				})
				.catch((error) =>
					message.error({
						// The backend returns actionable messages here — a duplicate group name
						// (409) and "Users are not part of this facility" (400) both land in this
						// path, so show them rather than a generic failure.
						content:
							typeof error === 'string' && error
								? error
								: isEdit
								? 'Failed to save user group.'
								: 'Failed to create user group.',
						key: messageKey,
					})
				);
		},
		[createUserGroup, isEdit, onSuccess, updateUserGroup, userGroup]
	);

	return (
		<Modal
			visible={true}
			width={700}
			title={isEdit ? 'Edit User Group' : 'Add User Group'}
			closable
			onCancel={onCancel}
			footer={[
				<OWButton key="cancel" type="default" size="large" onClick={onCancel}>
					Cancel
				</OWButton>,
				<OWButton
					key="submit"
					type="primary"
					size="large"
					style={{ marginLeft: 16 }}
					htmlType="submit"
					form={FORM_NAME}
					loading={saving}
				>
					{isEdit ? 'Save' : 'Create User Group'}
				</OWButton>,
			]}
		>
			<Form
				form={form}
				id={FORM_NAME}
				layout="vertical"
				requiredMark={false}
				initialValues={initialValues}
				onFinish={onSubmit}
			>
				<Form.Item
					name="name"
					label="Group Name"
					rules={[{ required: true, message: 'Please enter a group name' }]}
				>
					<Input placeholder="e.g. Facilities Leads" maxLength={255} />
				</Form.Item>

				{/* "Selected: X" and Clear All sit on the field's own header row, so the count
				    stays adjacent to the control it describes. */}
				<div className="mb-2 flex flex-row items-center justify-between">
					<span className="bodyregular text-character-primary">Users</span>
					<div className="ml-auto flex items-center gap-2">
						<span className="footnoteregular text-character-secondary">
							{`Selected: ${selectedCount}`}
						</span>
						{/* Already a link button; `px-0` drops antd's horizontal padding so the label
						    sits flush with the field's right edge. */}
						<Button
							type="link"
							className="px-0"
							onClick={clearAllUsers}
							disabled={selectedCount === 0}
						>
							Clear All
						</Button>
					</div>
				</div>
				<Form.Item
					name="memberEmails"
					rules={[{ required: true, message: 'Please select at least one user' }]}
				>
					<UserGroupMemberSelect placeholder="Select users" />
				</Form.Item>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	saving: state.user_groups.creating || state.user_groups.updating,
});

// Role-parameterised: the same modal serves /buyer/admin and /supplier/admin, and dispatching
// the wrong side would silently write to the other role's endpoint.
const mapDispatchToProps = (dispatch, ownProps) => ({
	createUserGroup: (entity) => dispatch(userGroupsRestCrudThunks(ownProps.userType).create(entity)),
	updateUserGroup: (entity) => dispatch(userGroupsRestCrudThunks(ownProps.userType).update(entity)),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(UserGroupFormModal));
